import { useTranslation } from 'next-i18next';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useDispatch } from 'react-redux';
import { useCustomTranslations } from '../../../../../hooks/useCustomTranslations';
import { useLanguage } from '../../../../../hooks/useLanguage';
import useMobileView from '../../../../../hooks/useMobileView';
import { usePages } from '../../../../../hooks/usePages';
import { FieldValuePair } from '../../../../../interfaces/interfaces';
import { CTAButtonComponentInterface, UiComponentElementInterface } from '../../../../../interfaces/pagebuilder';
import { setEventRegistration } from '../../../../../reduxToolkit/slices/commonSlice';
import { useSelector } from '../../../../../reduxToolkit/typedRedux';
import { updateRegistration } from '../../../../../services/services';
import { getToken } from '../../../../../utils/auth';
import { ConditionalWrapper } from '../../../../common/ConditionalWrapper';
import Link from 'next/link';

const CTAComponent = ({ uiComponent }: UiComponentElementInterface<CTAButtonComponentInterface>) => {
  const event = useSelector((state) => state.common.event);
  const pages = useSelector((state) => state.common.pages);
  const eventRegistration = useSelector((state) => state.common.eventRegistration);
  const guestmanagementFields = useSelector((state) => state.common.registrationFields);
  const [url, setURL] = useState<string | null>(null);
  const [eventPage, setEventPage] = useState<string>();
  const [isUpdating, setIsUpdating] = useState(false);

  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const { findPageById, switchPageWithPageData, getUrlForPageId } = usePages();
  const { ct } = useCustomTranslations();
  const { isMobileView } = useMobileView();
  const { t } = useTranslation();
  const { changeLanguage, getLanguage } = useLanguage();

  useEffect(() => {
    if (uiComponent?.url) {
      try {
        const urlParsed = new URL(uiComponent?.url);
        // we only append the token if the option is set in the uiComponent
        uiComponent?.appendToken && urlParsed.searchParams.set('access_token', getToken() || '');
        setURL(urlParsed.toString());
      } catch (e) {
        console.log(e);
        setURL(null);
      }
    } else {
      setURL(null);
    }
  }, [uiComponent]);

  useEffect(() => {
    if (
      (uiComponent?.isExternal === false && uiComponent?.buttonType === 'link') ||
      (uiComponent?.buttonType === 'data-update' && uiComponent?.dataUpdateAction === 'open_page')
    ) {
      setEventPage(uiComponent?.languageUrls?.[i18n.language] ?? uiComponent.eventpage);
    }
  }, [uiComponent]);

  async function handleDataUpdate() {
    if (
      (uiComponent?.dataUpdateFields && uiComponent?.dataUpdateFields?.length === 0) ||
      uiComponent?.dataUpdateFields?.find((field) => !field.value) ||
      uiComponent?.dataUpdateFields?.find((field) => !field.field)
    )
      return;
    if (!eventRegistration) {
      toast.error(t('event:registration.no-registration-data'));
      return;
    }

    setIsUpdating(true);

    const loading = toast.loading(t('common:loading'));

    try {
      const updatePayload = eventRegistration.registrationFields.map((field) => ({ field: field.field._id, value: field.value }));
      for (const field of uiComponent?.dataUpdateFields!) {
        const index = updatePayload.findIndex((f) => f.field === field.field);
        if (index >= 0) updatePayload.splice(index, 1);
        //@ts-ignore
        updatePayload.push(field);
      }

      const updatedRegistration = await updateRegistration(eventRegistration._id, {
        registrationFields: updatePayload,
      });
      dispatch(setEventRegistration(updatedRegistration.data));

      const languageProfileField = guestmanagementFields.find((entry) => entry.fieldType === 'language');
      if (languageProfileField) {
        const updatedLanguageField = updatePayload.find((field) => field.field === languageProfileField._id);
        if (updatedLanguageField) {
          /**
           * Fix fuer Messe Muenchen, deren Profilfelder mit Deutsch/Englisch anstatt de/en gesetzt sind und nicht mehr geaendert werden koennen, weil Tickets im Druck sind
           */
          let language = updatedLanguageField.value;
          if (language === 'Deutsch') language = 'de';
          else if (language === 'Englisch') language = 'en';

          if (getLanguage() !== language) await changeLanguage(language);
        }
      }
    } catch (error) {
      console.error(error);
    }

    setIsUpdating(false);
    toast.dismiss(loading);
  }

  function hasFieldValue(dataField: FieldValuePair) {
    return dataField.value === eventRegistration?.registrationFields.find((field) => field.field._id === dataField.field)?.value;
  }

  const isSelected = uiComponent?.buttonType === 'data-update' && !uiComponent?.dataUpdateFields.find((field) => !hasFieldValue(field));

  function getLink() {
    const isExternalLink = uiComponent?.isExternal && uiComponent?.buttonType === 'link';
    const isExternalDataUpdate = uiComponent?.dataUpdateAction === 'open_url' && uiComponent?.buttonType === 'data-update';

    if (isExternalLink || isExternalDataUpdate) {
      return url || '';
    }

    if (!eventPage) console.log('No eventpage set, probably none exists for this language');

    return getUrlForPageId(eventPage, pages, event?.slug);
  }

  async function handleClick() {
    if (!uiComponent || (uiComponent?.buttonType === 'link' && (uiComponent?.isExternal || uiComponent?.newTab)) || !event || !uiComponent?.buttonType) return;
    if (uiComponent?.buttonType === 'data-update') await handleDataUpdate();
    if (uiComponent?.buttonType === 'data-update' && (uiComponent.dataUpdateAction === 'nothing' || uiComponent?.newTab)) return;
    const page = findPageById(eventPage!, pages);
    if (!page) return;

    switchPageWithPageData(page, event.slug, true);
  }

  function shouldWrapWithA() {
    if (!uiComponent) return false;
    if (isUpdating) return false;

    if (uiComponent.buttonType === 'link' && uiComponent.isExternal && !!url) {
      return true;
    } else if (uiComponent.buttonType === 'link' && !uiComponent.isExternal && !!eventPage) {
      return true;
    } else if (uiComponent.buttonType === 'data-update' && uiComponent.dataUpdateAction === 'open_url' && !!url) {
      return true;
    } else if (uiComponent.buttonType === 'data-update' && uiComponent.dataUpdateAction === 'open_page') {
      return true;
    } else {
      return false;
    }
  }

  return (
    <div className={`grid justify-items-center overflow-x-hidden`}>
      <ConditionalWrapper
        condition={shouldWrapWithA()}
        wrapper={(children) => (
          <Link
            target={uiComponent?.newTab ? '_blank' : '_self'}
            href={getLink()}
            shallow={(!uiComponent?.isExternal || uiComponent.dataUpdateAction === 'open_page') && !uiComponent?.newTab}
            style={{ width: '100%', display: 'grid', placeItems: 'center' }}>
            {children}
          </Link>
        )}>
        <button
          disabled={isUpdating}
          onClick={handleClick}
          className={`${!uiComponent?.customStyling.buttonBorderRadius ? 'templateBorder' : ''} ctaButton ${isUpdating ? 'animate-pulse' : 'cursor-pointer'} ${
            isSelected ? 'border-4 ' : ''
          } ${isMobileView() ? '' : 'transform transition duration-200 hover:scale-105'}`}>
          {ct(uiComponent?.title || 'CTA Button', uiComponent)}
        </button>
      </ConditionalWrapper>
      <style jsx>
        {`
          .wrapper {
            color: ${uiComponent?.styling?.textColor || event?.template?.mainButtonTextColor};
            width: 100%;
            display: grid;
            place-items: center;
            overflow-x: hidden;
          }

          .ctaButton {
            margin: 0.5em;
            padding: 0.8rem 3rem;
            border-radius: ${uiComponent?.customStyling?.buttonBorderRadius ? `${uiComponent?.customStyling?.buttonBorderRadius}px` : '5px'};
            box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.16);
            font-size: ${uiComponent?.customStyling?.buttonFontSize ? `${uiComponent?.customStyling?.buttonFontSize}px` : '1.2rem'};
            outline: none;
            color: ${uiComponent?.styling?.textColor || '#000'};
            background-color: ${uiComponent?.customStyling?.buttonBackgroundColor && !uiComponent?.customStyling?.buttonBackgroundColorIsDefault
              ? uiComponent?.customStyling?.buttonBackgroundColor
              : event?.template?.mainColor} !important;
            height: ${uiComponent?.customStyling?.height ? `${uiComponent?.customStyling?.height}px` : ''};
            max-width: 100%;
            width: ${isMobileView() ? '100%' : uiComponent?.customStyling?.width ? `${uiComponent?.customStyling?.width}px` : ''};
          }
        `}
      </style>
    </div>
  );
};

export default CTAComponent;
